import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, ConfirmDialog, useToast } from "@ozpanel/ui";
import { StatusPill } from "./PageHeader";

// ActionCard — one described operation, and what the server said when it ran.
//
// The existing ActionBar/RowActions cover actions ON A ROW. This is the other shape, and it is
// most of the operational surface: a standalone operation with no row to hang off — claim the
// master key, pull secrets, sign an update, promote a survivor, re-issue an identity.
//
// Two decisions worth stating, both learned from this platform's own reports:
//
// 1. THE RESULT IS RENDERED, not reduced to a toast. These endpoints answer with structured
//    accounts of what they did and what they could not do — eviction returns done/failed/pending
//    lists precisely because "some depths cannot be closed by any automated path". A toast saying
//    "Evicted" throws away the half of the answer that needed reading, and the operator then
//    believes the removal was complete. Anything the server returns is shown until dismissed.
//
// 2. THE CONFIRMATION CARRIES THE SERVER'S OWN WORDS where there are any. A generic "are you
//    sure?" trains people to click through; the endpoint's own description of consequences does
//    not.
export interface ActionCardProps {
  title: string;
  /** What this does and when to use it. Required — an action nobody can explain is one nobody
   *  should be able to fire from a console. */
  description: React.ReactNode;
  actionLabel: string;
  danger?: "low" | "medium" | "high";
  /** Shown instead of the button when the action cannot apply here, with the reason. A disabled
   *  button with no explanation is the worst of both: it looks broken and teaches nothing. */
  unavailable?: React.ReactNode;
  /** PREFLIGHT — what was checked, shown above the confirm, never a veto.
   *
   *  `unavailable` disables a control outright, and that is the wrong shape for almost every
   *  dangerous action here: it decides FOR the operator using a reading that may itself be wrong.
   *  "Promote this node as the survivor" is the clearest case — the probe that reports a peer as
   *  answering runs on a node that may be the partitioned half, and if it is wrong the block
   *  removes the only recovery action at the only moment it matters.
   *
   *  So preflight keeps the interlock and drops the veto: the findings are put in front of the
   *  operator, and they proceed knowing what was measured. A failing check is shown as a warning,
   *  not a locked door — the judgement of whether the peer is really dead is theirs, and only they
   *  have it.
   *
   *  `unavailable` stays for the narrow case where the SERVER would refuse anyway: a button that
   *  can only answer 409 should not be offered. */
  preflight?: { ok: boolean; label: string; detail?: string }[];
  confirm?: { title: string; body?: React.ReactNode; confirmLabel?: string };
  run: () => Promise<unknown>;
  /** Query keys to refresh once it succeeds. */
  invalidates?: unknown[][];
  /** Turn the server's response into something readable. Defaults to formatted JSON. */
  renderResult?: (result: unknown) => React.ReactNode;
}

export function ActionCard({
  title,
  description,
  actionLabel,
  danger = "low",
  unavailable,
  preflight,
  confirm: confirmGiven,
  run,
  invalidates,
  renderResult,
}: ActionCardProps) {
  // A destructive card ALWAYS asks, whether or not its caller remembered to say how: the default
  // names the act and repeats what it does.
  const confirm = confirmGiven ?? (danger === "high" ? { title: `${title}?`, body: description } : undefined);
  const qc = useQueryClient();
  const { notify } = useToast();
  const [asking, setAsking] = React.useState(false);
  const [result, setResult] = React.useState<unknown>(null);
  const [failed, setFailed] = React.useState<string | null>(null);

  const m = useMutation({
    mutationFn: run,
    onSuccess: (r) => {
      setFailed(null);
      setResult(r ?? { ok: true });
      for (const k of invalidates ?? []) void qc.invalidateQueries({ queryKey: k });
      notify({ kind: "success", title: `${title} completed` });
    },
    onError: (e: unknown) => {
      setResult(null);
      setFailed(e instanceof Error ? e.message : String(e));
      notify({ kind: "error", title: `${title} failed` });
    },
  });

  const askOrRun = () => (confirm ? setAsking(true) : m.mutate());

  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[13px] font-medium">
            {title}
            {danger === "high" && <StatusPill tone="bad">destructive</StatusPill>}
          </div>
          <div className="mt-1 text-[12px] leading-relaxed text-muted">{description}</div>
        </div>
        <div className="shrink-0">
          {unavailable ? (
            <span className="text-[12px] text-muted">unavailable</span>
          ) : (
            <Button
              size="sm"
              variant={danger === "high" ? "danger" : "default"}
              onClick={() => askOrRun()}
              disabled={m.isPending}
            >
              {m.isPending ? "Working…" : actionLabel}
            </Button>
          )}
        </div>
      </div>

      {/* The reason it cannot run, in place of the button that would have run it. */}
      {unavailable && <div className="mt-2 rounded border border-line bg-bg2 px-3 py-2 text-[12px] text-muted">{unavailable}</div>}

      {failed && (
        <div className="mt-3 rounded border border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] bg-critsoft px-3 py-2 text-[12px] leading-relaxed text-crit">
          {failed}
        </div>
      )}

      {result != null && (
        <div className="mt-3 rounded border border-line bg-bg2">
          <div className="flex items-center justify-between border-b border-line px-3 py-1.5">
            <span className="text-[11.5px] uppercase tracking-wide text-muted">What the server reported</span>
            <button type="button" className="text-[11.5px] text-muted hover:text-fg" onClick={() => setResult(null)}>
              dismiss
            </button>
          </div>
          <div className="px-3 py-2 text-[12px] leading-relaxed">
            {renderResult ? renderResult(result) : <ResultJSON value={result} />}
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          open={asking}
          onOpenChange={setAsking}
          title={confirm.title}
          body={
            <div className="space-y-2">
              {/* Findings FIRST: the operator reads what was measured before the description of
                  what the action does, because the measurement is what decides whether now is the
                  right moment. A failing check is a warning here, never a locked door. */}
              {preflight && preflight.length > 0 && (
                <div className="space-y-1 rounded border border-line bg-bg2 px-2 py-1.5">
                  <div className="text-[11px] uppercase tracking-wide text-muted">Checked before running</div>
                  {preflight.map((c) => (
                    <div key={c.label} className="flex items-start gap-2 text-[12px] leading-relaxed">
                      <span className={c.ok ? "text-ok" : "text-warn"}>{c.ok ? "✓" : "!"}</span>
                      <span>
                        <span className={c.ok ? "" : "font-medium"}>{c.label}</span>
                        {c.detail ? <span className="text-muted"> — {c.detail}</span> : null}
                      </span>
                    </div>
                  ))}
                  {preflight.some((c) => !c.ok) && (
                    <div className="pt-1 text-[11.5px] leading-relaxed text-muted">
                      Not a refusal. These are what this node could measure from where it stands —
                      you may know something it cannot, and proceeding is yours to decide.
                    </div>
                  )}
                </div>
              )}
              {confirm.body}
            </div>
          }
          confirmLabel={confirm.confirmLabel ?? actionLabel}
          destructive={danger === "high"}
          onConfirm={() => {
            setAsking(false);
            m.mutate();
          }}
        />
      )}
    </div>
  );
}

// ResultJSON prints the server's answer without pretending to understand it.
//
// A generic renderer that guessed at structure would be wrong for most of these endpoints and
// would hide fields it did not expect — which is precisely how a partial failure becomes
// invisible. Showing everything is the honest default; callers that know the shape pass
// renderResult.
function ResultJSON({ value }: { value: unknown }) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    const lists = Object.entries(obj).filter(([, v]) => Array.isArray(v) && (v as unknown[]).length > 0);
    const scalars = Object.entries(obj).filter(([, v]) => v === null || typeof v !== "object");
    if (lists.length || scalars.length) {
      return (
        <div className="space-y-2">
          {scalars.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {scalars.map(([k, v]) => (
                <span key={k} className="rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-[11px]">
                  {k} <span className="text-accent">{String(v)}</span>
                </span>
              ))}
            </div>
          )}
          {lists.map(([k, v]) => (
            <div key={k}>
              <div className="mb-1 text-[11.5px] uppercase tracking-wide text-muted">{k.replace(/_/g, " ")}</div>
              <ul className="list-inside list-disc space-y-0.5">
                {(v as unknown[]).map((item, i) => (
                  <li key={i} className="text-[12px] leading-relaxed">
                    {typeof item === "object" ? JSON.stringify(item) : String(item)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      );
    }
  }
  return <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11.5px]">{JSON.stringify(value, null, 2)}</pre>;
}
